import { useCallback, useState } from "react"
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { cn } from "@/lib/utils"
import { MetricLabel } from "../MetricLabel/MetricLabel"

export interface BalanceDrawdownPoint {
  label: string
  balance: number
  drawdown: number
  // Unix timestamp in seconds. When present on every point in `balance`,
  // RiskView renders the Balance & Drawdown chart with a numeric time
  // X-axis so the hover dots track the cursor smoothly even when several
  // points share the same MM/DD label.
  time?: number
}

export interface StreakMetric {
  label: string
  positive: number
  negative: number
  type: "count" | "money"
  definition?: string
}

export interface MfeMaePoint {
  trade: number
  mfe: number
  mae: number
  avgProfit: number
  avgLoss: number
}

export interface MfeMaeMode {
  id: string
  label: string
  data: MfeMaePoint[]
  format: "pct" | "money"
}

export interface RiskViewProps {
  balance: BalanceDrawdownPoint[]
  streaks: StreakMetric[]
  mfeMaeModes: MfeMaeMode[]
  defaultMfeMaeId?: string
  className?: string
}

const TOOLTIP_STYLE = {
  background: "var(--popover)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  fontSize: 11,
  color: "var(--foreground)",
  padding: "6px 8px",
} as const

const BALANCE_COLOR = "#26a69a"
const DD_COLOR = "#f23645"
const MFE_LIGHT = "rgba(38, 166, 154, 0.35)"
const MFE_SOLID = "#26a69a"
const MAE_LIGHT = "rgba(242, 54, 69, 0.35)"
const MAE_SOLID = "#f23645"

function formatStreak(v: number, type: "count" | "money") {
  if (type === "money") {
    return `${v >= 0 ? "+" : "-"}$${Math.abs(v).toFixed(1)}`
  }
  return `${Math.round(v)}`
}

function labelForTime(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000)
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0")
  const dd = String(d.getUTCDate()).padStart(2, "0")
  return `${mm}/${dd}`
}

export function RiskView({
  balance,
  streaks,
  mfeMaeModes,
  defaultMfeMaeId,
  className,
}: RiskViewProps) {
  const [mfeMaeId, setMfeMaeId] = useState<string>(
    defaultMfeMaeId ?? mfeMaeModes[0]?.id ?? "",
  )
  const handleMode = useCallback((id: string) => setMfeMaeId(id), [])
  const currentMode = mfeMaeModes.find((m) => m.id === mfeMaeId) ?? mfeMaeModes[0]
  const yFmt = currentMode?.format === "money"
    ? (v: number) => `$${v.toFixed(0)}`
    : (v: number) => `${v.toFixed(1)}%`

  // Enable the numeric/time X-axis only when every point carries a
  // timestamp. Storybook fixtures and the prototype mock pass plain
  // string labels (`T1`, `T2`, ...) without a `time` field, so they fall
  // back to the categorical axis below.
  const useTimeAxis = balance.length > 0 && balance.every(
    (p) => typeof p.time === "number",
  )

  return (
    <div className={className}>
      <div className="border-b border-border px-4 py-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-section text-foreground">Balance &amp; Drawdown</div>
          <div className="flex items-center gap-3 text-caption text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: BALANCE_COLOR }} />
              Balance
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DD_COLOR }} />
              Drawdown
            </span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <ComposedChart data={balance} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="balFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={BALANCE_COLOR} stopOpacity={0.15} />
                <stop offset="100%" stopColor={BALANCE_COLOR} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="ddFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={DD_COLOR} stopOpacity={0} />
                <stop offset="100%" stopColor={DD_COLOR} stopOpacity={0.15} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            {useTimeAxis ? (
              <XAxis
                dataKey="time"
                type="number"
                scale="time"
                domain={["dataMin", "dataMax"]}
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
                tickFormatter={(t: number) => labelForTime(t)}
              />
            ) : (
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
              />
            )}
            <YAxis
              yAxisId="balance"
              tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
              width={50}
              axisLine={{ stroke: "var(--border)" }}
              tickLine={false}
              tickFormatter={(v) => `$${(v / 1e3).toFixed(0)}k`}
            />
            <YAxis
              yAxisId="dd"
              orientation="right"
              tick={{ fontSize: 11, fill: DD_COLOR }}
              width={40}
              axisLine={{ stroke: "var(--border)" }}
              tickLine={false}
              tickFormatter={(v) => `${v.toFixed(0)}%`}
              reversed
            />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              labelStyle={{ color: "var(--muted-foreground)", marginBottom: 2 }}
              cursor={{ fill: "transparent" }}
              labelFormatter={useTimeAxis
                ? (t) => labelForTime(Number(t))
                : undefined}
            />
            <Area
              yAxisId="balance"
              type="monotone"
              dataKey="balance"
              stroke={BALANCE_COLOR}
              strokeWidth={1.5}
              fill="url(#balFill)"
              isAnimationActive={false}
            />
            <Area
              yAxisId="dd"
              type="monotone"
              dataKey="drawdown"
              stroke={DD_COLOR}
              strokeWidth={1.5}
              fill="url(#ddFill)"
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="border-b border-border px-4 py-4">
        <div className="text-section text-foreground mb-3">Streaks</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {streaks.map((s) => (
            <div key={s.label} className="flex flex-col items-center text-center">
              <MetricLabel
                label={s.label}
                definition={s.definition}
                className="text-caption text-muted-foreground mb-1"
              />
              <div className="flex items-center gap-2">
                <span className="text-body text-success">{formatStreak(s.positive, s.type)}</span>
                <span className="text-caption text-muted-foreground">/</span>
                <span className="text-body text-danger">{formatStreak(s.negative, s.type)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {currentMode && (
        <div className="border-b border-border px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-section text-foreground">MFE / MAE</div>
              <div className="text-caption text-muted-foreground">
                Max Favorable and Adverse Excursion per trade
              </div>
            </div>
            <div className="flex items-center gap-1">
              {mfeMaeModes.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handleMode(m.id)}
                  className={cn(
                    "text-caption px-2 py-0.5 rounded cursor-pointer transition-colors",
                    mfeMaeId === m.id
                      ? "bg-muted text-foreground font-medium"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 mb-2 text-caption text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: MFE_LIGHT }} />
              MFE
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: MFE_SOLID }} />
              Avg Profit
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: MAE_SOLID }} />
              Avg Loss
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: MAE_LIGHT }} />
              MAE
            </span>
          </div>

          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={currentMode.data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="trade"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                width={45}
                axisLine={{ stroke: "var(--border)" }}
                tickLine={false}
                tickFormatter={yFmt}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: "var(--muted-foreground)", marginBottom: 2 }}
                cursor={{ fill: "transparent" }}
              />
              <Bar dataKey="mfe" name="MFE" fill={MFE_LIGHT} />
              <Bar dataKey="avgProfit" name="Avg Profit" fill={MFE_SOLID} />
              <Bar dataKey="avgLoss" name="Avg Loss" fill={MAE_SOLID} />
              <Bar dataKey="mae" name="MAE" fill={MAE_LIGHT} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
